import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { portalApi } from "@/lib/portalApi";
import PennyWidget from "@/components/portal/PennyWidget";
import PortalViewBanner from "@/components/portal/PortalViewBanner";

const DEFAULT_BRANDING = { branding_color: "#171B2E", welcome_message: "Welcome to your secure client portal.", firm_name: "Expat Tax Firm" };

export function PortalBrand({ light = false, logoUrl = "", firmName = "" }) {
  const [imgOk, setImgOk] = useState(true);
  const name = firmName || "EXPAT TAX FIRM";
  if (logoUrl && imgOk) {
    return (
      <div className="flex items-center gap-2.5 shrink-0" data-testid="portal-brand">
        <span className="bg-white rounded-lg p-1 shadow-sm inline-flex items-center">
          <img src={logoUrl} alt={name} onError={() => setImgOk(false)} className="h-8 max-w-[150px] object-contain" data-testid="portal-brand-logo" />
        </span>
        <div className="leading-none hidden sm:block whitespace-nowrap">
          <div className={`font-heading text-sm font-bold tracking-tight ${light ? "text-white" : "text-charcoal"}`}>{name}</div>
          <div className={`text-[10px] tracking-[0.2em] uppercase ${light ? "text-slate-400" : "text-slate-500"}`}>Client Portal</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5 shrink-0" data-testid="portal-brand">
      <div className="h-9 w-9 rounded-full bg-brand-gold flex items-center justify-center shadow-sm shrink-0">
        <Icons.DollarSign className="h-5 w-5 text-charcoal" strokeWidth={3} />
      </div>
      <div className="leading-none whitespace-nowrap">
        <div className={`font-heading text-base font-extrabold tracking-tight ${light ? "text-white" : "text-charcoal"}`}>
          {firmName ? name : <>EXPAT <span className="text-brand-gold">TAX</span> FIRM</>}
        </div>
        <div className={`text-[10px] tracking-[0.2em] uppercase ${light ? "text-slate-400" : "text-slate-500"}`}>Client Portal</div>
      </div>
    </div>
  );
}

// Apply a client brand's accent to portal CSS vars so gold accents + Penny re-theme.
export function applyPortalBrand(brand) {
  if (typeof document === "undefined" || !brand) return;
  const root = document.documentElement;
  if (brand.accent_rgb) root.style.setProperty("--brand-gold", brand.accent_rgb);
  if (brand.accent_dark_rgb) root.style.setProperty("--brand-gold-dark", brand.accent_dark_rgb);
}

// "Account & Preferences" removed from primary nav — accessible via the user
// avatar/name in the header row, which is a NavLink to /portal/profile.
const NAV = [
  ["/portal", "Dashboard", "LayoutDashboard", true, "dashboard"],
  ["/portal/requests", "Requests", "ClipboardList", false, "requests"],
  ["/portal/scheduling", "Scheduling", "CalendarClock", false, "scheduling"],
  ["/portal/documents", "Documents", "FolderOpen", false, "documents"],
  ["/portal/strategic-reviews", "Strategic Reviews", "FileCheck", false, "strategic-reviews"],
  ["/portal/billing", "Billing", "Receipt", false, "billing"],
  ["/portal/messages", "Messages", "MessageSquare", false, "messages"],
  ["/portal/tax-profile", "Tax Profile", "FileUser", false, "tax-profile"],
];

export default function PortalLayout() {
  const { profile, logout } = usePortalAuth();
  const navigate = useNavigate();
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [taxAttention, setTaxAttention] = useState(false);
  const initials = (profile?.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  useEffect(() => {
    portalApi.get("/portal/branding")
      .then((r) => setBranding({ ...DEFAULT_BRANDING, ...(r.data || {}) }))
      .catch(() => {});
    // Nav badge reflects a genuine Tax Profile attention state (fail-open).
    portalApi.get("/portal/action-center")
      .then((r) => setTaxAttention((r.data?.items || []).some((it) => it.key === "tax_profile")))
      .catch(() => {});
  }, []);

  // Entry-point brand: the client's own firm brand drives the portal look.
  const brand = profile?.brand;
  useEffect(() => { applyPortalBrand(brand); }, [brand]);

  const doLogout = () => { logout(); navigate("/portal/login"); };
  const brandColor = brand?.header || branding.branding_color || DEFAULT_BRANDING.branding_color;
  const logoUrl = brand?.logo_url || branding.firm_logo_url;
  const firmName = brand?.name || branding.firm_name;

  return (
    <div className="min-h-screen bg-[#F6F7FB]" data-testid="portal-layout">
      {profile?.portal_view?.active && <PortalViewBanner view={profile.portal_view} />}
      <header className="text-white sticky top-0 z-30 shadow-md" style={{ backgroundColor: brandColor }} data-testid="portal-header">

        {/* ── Row 1: Brand + Account Identity ── */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4" data-testid="portal-header-row1">
          <PortalBrand light logoUrl={logoUrl} firmName={firmName} />
          <div className="flex items-center gap-3 shrink-0">
            <NavLink to="/portal/profile" data-testid="portal-account-link"
              className="hidden sm:flex items-center gap-2 rounded-lg px-2 py-1 text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
              title="Account & Preferences">
              <span className="text-sm font-medium whitespace-nowrap" data-testid="portal-user-name">{profile?.full_name}</span>
              <div className="h-8 w-8 rounded-full bg-brand-gold text-charcoal flex items-center justify-center text-sm font-bold shrink-0">{initials}</div>
            </NavLink>
            {/* Mobile: avatar only links to account */}
            <NavLink to="/portal/profile" className="sm:hidden h-8 w-8 rounded-full bg-brand-gold text-charcoal flex items-center justify-center text-sm font-bold shrink-0"
              data-testid="portal-account-link-mobile" title="Account & Preferences">{initials}</NavLink>
            <button onClick={doLogout} data-testid="portal-logout" className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10" title="Sign out" aria-label="Sign out">
              <Icons.LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Row 2: Desktop Navigation (flex-wrap — no horizontal scroll) ── */}
        <div className="hidden md:block border-t border-white/10" data-testid="portal-nav-row">
          <nav className="max-w-6xl mx-auto px-4 sm:px-6 py-1 flex flex-wrap items-center gap-0.5">
            {NAV.map(([to, label, icon, end, slug]) => (
              <NavLink key={to} to={to} end={end} data-testid={`portal-nav-${slug}`}
                className={({ isActive }) =>
                  `relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-brand-gold text-charcoal" : "text-slate-300 hover:text-white hover:bg-white/10"
                  }`}>
                {(() => { const Ic = Icons[icon]; return <Ic className="h-4 w-4" />; })()}
                {label}
                {slug === "tax-profile" && taxAttention && (
                  <span data-testid="portal-nav-tax-profile-badge"
                    aria-label="Tax Profile, attention recommended"
                    className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#F5A623] text-charcoal text-[11px] font-bold">
                    1
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* ── Mobile Navigation (flex-wrap — no horizontal scroll) ── */}
        <nav className="md:hidden flex flex-wrap items-center gap-1 px-3 pt-1 pb-2 border-t border-white/10">
          {NAV.map(([to, label, icon, end, slug]) => (
            <NavLink key={to} to={to} end={end} data-testid={`portal-nav-m-${slug}`}
              className={({ isActive }) =>
                `relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                  isActive ? "bg-brand-gold text-charcoal" : "text-slate-300 bg-white/5"
                }`}>
              {(() => { const Ic = Icons[icon]; return <Ic className="h-3.5 w-3.5" />; })()}
              {label}
              {slug === "tax-profile" && taxAttention && (
                <span data-testid="portal-nav-m-tax-profile-badge" aria-label="Tax Profile, attention recommended"
                  className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#F5A623] text-charcoal text-[10px] font-bold">1</span>
              )}
            </NavLink>
          ))}
        </nav>

      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-7" data-testid="portal-main">
        <Outlet context={{ branding }} />
      </main>

      <PennyWidget />
    </div>
  );
}
